import { ReactNode, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

interface TooltipProps {
  text: ReactNode;
  children: ReactNode;
}

export default function Tooltip({ text, children }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isAbove, setIsAbove] = useState(false);
  const iconRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const show = () => {
    clearHideTimeout();
    setIsVisible(true);
  };

  const hide = () => {
    clearHideTimeout();
    // Brief delay so the pointer can move onto the tooltip (e.g. to click a link)
    hideTimeoutRef.current = setTimeout(() => setIsVisible(false), 150);
  };

  useEffect(() => {
    return () => clearHideTimeout();
  }, []);

  useEffect(() => {
    if (!isVisible || !iconRef.current) return;

    const updatePosition = () => {
      if (!iconRef.current) return;
      const rect = iconRef.current.getBoundingClientRect();
      const tooltipHeight = tooltipRef.current?.offsetHeight || 160;
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Prefer below; flip above when there isn't enough room
      const shouldPositionAbove =
        spaceBelow < tooltipHeight + 8 && spaceAbove > spaceBelow;

      setIsAbove(shouldPositionAbove);
      setPosition({
        top: shouldPositionAbove
          ? rect.top + window.scrollY - tooltipHeight - 8
          : rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX + rect.width / 2,
      });
    };

    updatePosition();
    const frame = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(frame);
  }, [isVisible, text]);

  return (
    <div className="relative inline-flex items-center">
      {children}
      <div
        ref={iconRef}
        className="relative ml-2"
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        <InformationCircleIcon className="h-5 w-5 text-gray-400 cursor-help" />
      </div>
      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            className="fixed z-50 w-72 p-3 bg-gray-800 text-white text-sm rounded-lg shadow-lg transform -translate-x-1/2"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
            }}
            onMouseEnter={show}
            onMouseLeave={hide}
          >
            {text}
            <div
              className={`absolute w-3 h-3 bg-gray-800 transform rotate-45 left-1/2 -translate-x-1/2 ${
                isAbove ? "-bottom-1.5" : "-top-1.5"
              }`}
            ></div>
          </div>,
          document.body
        )}
    </div>
  );
}
